import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface FileStorageDB extends DBSchema {
    files: {
        key: string;
        value: File;
    };
}

let dbPromise: Promise<IDBPDatabase<FileStorageDB>> | null = null;

const getDB = (): Promise<IDBPDatabase<FileStorageDB>> => {
    if (!dbPromise) {
        dbPromise = openDB<FileStorageDB>('fileStorage', 1, {
            upgrade(db) {
                db.createObjectStore('files');
            },
        });
    }
    return dbPromise;
};

export const storeFileInIndexedDB = async (file: File): Promise<string> => {
    try {
        const db = await getDB();
        const fileKey = `pdf_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        await db.put('files', file, fileKey);
        return fileKey;
    } catch (error) {
        console.error('Error storing file in IndexedDB:', error);
        throw new Error('Failed to store file');
    }
};

export const getFileFromIndexedDB = async (fileKey: string): Promise<File | null> => {
    try {
        const db = await getDB();
        const file = await db.get('files', fileKey);
        return file || null;
    } catch (error) {
        console.error('Error retrieving file from IndexedDB:', error);
        return null;
    }
};

export const removeFileFromIndexedDB = async (fileKey: string): Promise<void> => {
    try {
        const db = await getDB();
        await db.delete('files', fileKey);
    } catch (error) {
        console.error('Error removing file from IndexedDB:', error);
    }
};

export const clearAllFilesFromIndexedDB = async (): Promise<void> => {
    try {
        const db = await getDB();
        await db.clear('files');
    } catch (error) {
        console.error('Error clearing files from IndexedDB:', error);
    }
};

export const listAllFilesInIndexedDB = async (): Promise<string[]> => {
    try {
        const db = await getDB();
        const keys = await db.getAllKeys('files');

        // Check storage quota
        if ('storage' in navigator && 'estimate' in navigator.storage) {
            const estimate = await navigator.storage.estimate();
        }

        return keys;
    } catch (error) {
        console.error('Error listing files from IndexedDB:', error);
        return [];
    }
};

// Clean up old files (older than 1 hour)
export const cleanupOldFiles = async (): Promise<void> => {
    try {
        const db = await getDB();
        const keys = await db.getAllKeys('files');
        const oneHourAgo = Date.now() - 60 * 60 * 1000; // 1 hour ago

        for (const key of keys) {
            // Extract timestamp from key (pdf_timestamp_random)
            const timestamp = parseInt(key.split('_')[1]);
            if (timestamp < oneHourAgo) {
                await db.delete('files', key);
            }
        }
    } catch (error) {
        console.error('Error cleaning up old files:', error);
    }
};
